// HQ Inventory module (Phase 1).
//   Tab 1 — Products & QR labels (list, create, print QR sheet)
//   Tab 2 — Outlets        (list, create, assign)
//   Tab 3 — Prepare Delivery (HQ → outlet, scan or pick products, dispatch)
//   Tab 4 — Dispatch Tracker (status, outstanding, drill into history)
//   Tab 5 — Inventory Dashboard (per-outlet balances, ledger drill-down)
//
// Scope locked with operator: multi-outlet company-owned, QR scanning,
// mobile receiving. Partnership pricing / Stocktake / Wastage ship in P2+.
import { useEffect, useMemo, useState } from "react";
import { api } from "@/staff/api";
import MgmtLayout from "@/staff/management/MgmtLayout";
import { Download, Plus, Package, Store, Truck, ChartBar, QrCode, X, Loader2 } from "lucide-react";

const inputCls = "px-3 py-2 rounded-sm border border-ink/15 bg-white text-sm text-ink focus:outline-none focus:border-matcha-dark";
const money = (n) => Number(n || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MgmtInventory() {
  const [tab, setTab] = useState("products");
  return (
    <MgmtLayout title="Inventory" subtitle="Phase 1 · multi-outlet stock">
      <div className="flex flex-wrap gap-2 mb-6 border-b border-ink/10 pb-2" data-testid="inv-tabs">
        {[
          ["products", "Products", <Package size={14} />],
          ["outlets",  "Outlets",  <Store size={14} />],
          ["dispatch", "Prepare Delivery", <Truck size={14} />],
          ["tracker",  "Dispatch Tracker", <ChartBar size={14} />],
          ["balances", "On-Hand Dashboard", <ChartBar size={14} />],
        ].map(([k, label, icon]) => (
          <button
            key={k}
            type="button"
            data-testid={`inv-tab-${k}`}
            onClick={() => setTab(k)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-[0.18em] rounded-full transition-colors ${
              tab === k ? "bg-ink text-bone" : "text-ink-soft hover:text-ink hover:bg-ink/5"
            }`}
          >
            {icon} {label}
          </button>
        ))}
      </div>
      {tab === "products" && <ProductsTab />}
      {tab === "outlets"  && <OutletsTab />}
      {tab === "dispatch" && <DispatchTab />}
      {tab === "tracker"  && <TrackerTab />}
      {tab === "balances" && <BalancesTab />}
    </MgmtLayout>
  );
}


// =========================== PRODUCTS ===========================
function ProductsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [qrFor, setQrFor] = useState(null);
  const [err, setErr] = useState("");
  const [outlets, setOutlets] = useState([]);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const load = async () => {
    try {
      const [{ data: prods }, { data: outs }] = await Promise.all([
        api.get("/inv/products"),
        api.get("/inv/outlets"),
      ]);
      setRows(prods);
      setOutlets(outs || []);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, []);

  const runSync = async (outlet_code) => {
    setSyncBusy(true); setErr(""); setSyncResult(null);
    try {
      const { data } = await api.post("/inv/products/migrate-from-ingredients", { outlet_code });
      await load();
      setSyncResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setSyncOpen(false);
    } finally {
      setSyncBusy(false);
    }
  };

  const printSheet = async () => {
    if (rows.length === 0) return;
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("matchastory.token");
    // Pre-fetch each QR as a base64 data URL (the print iframe has no
    // access to our Authorization header, so inlining is the simplest fix).
    const items = await Promise.all(rows.map(async (p) => {
      try {
        const resp = await fetch(`${apiBase}/api/inv/products/${p.sku}/qr.png`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const blob = await resp.blob();
        const dataUrl = await new Promise((res) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.readAsDataURL(blob);
        });
        return { ...p, dataUrl };
      } catch { return { ...p, dataUrl: "" }; }
    }));
    const html = buildQrSheetHTML(items);
    let f = document.getElementById("inv-print-frame");
    if (!f) {
      f = document.createElement("iframe");
      f.id = "inv-print-frame";
      f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
      document.body.appendChild(f);
    }
    const d = f.contentDocument;
    d.open(); d.write(html); d.close();
    const trigger = () => { try { f.contentWindow.focus(); f.contentWindow.print(); } catch {} };
    if (d.readyState === "complete") setTimeout(trigger, 200);
    else f.onload = () => setTimeout(trigger, 200);
  };

  return (
    <div className="space-y-4" data-testid="inv-products-pane">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-xl text-ink">Product Catalogue · {rows.length} SKUs</h2>
        <div className="ml-auto flex gap-2">
          <button type="button" data-testid="inv-migrate-ingredients" onClick={() => { setSyncResult(null); setSyncOpen(true); }} className="px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-matcha-dark hover:text-ink border border-matcha-dark/30 rounded-full">Sync from Ingredients</button>
          <button type="button" data-testid="inv-qr-print" onClick={printSheet} disabled={rows.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-ink text-bone text-[11px] uppercase tracking-[0.22em] rounded-full disabled:opacity-40">
            <Download size={12} /> Print QR Sheet
          </button>
          <button type="button" data-testid="inv-product-new" onClick={() => setForm({})}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 border border-ink/20 text-ink text-[11px] uppercase tracking-[0.22em] rounded-full hover:bg-ink/5">
            <Plus size={12} /> New SKU
          </button>
        </div>
      </div>
      {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm">{err}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="inv-products-table">
          <thead className="text-left text-ink-soft border-b border-ink/10">
            <tr>
              <th className="py-2 overline">SKU</th>
              <th className="py-2 overline">Name</th>
              <th className="py-2 overline">Category</th>
              <th className="py-2 overline">Base</th>
              <th className="py-2 overline">Purchase</th>
              <th className="py-2 overline">Supplier</th>
              <th className="py-2 overline text-right">Cost</th>
              <th className="py-2 overline">QR</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="py-6 text-center text-ink-soft">No products yet. Click <strong>Seed samples</strong> to populate three outlets and five Matcha Story SKUs.</td></tr>}
            {rows.map((p) => (
              <tr key={p.sku} className="border-t border-ink/5" data-testid={`inv-product-row-${p.sku}`}>
                <td className="py-2 font-mono text-ink">{p.sku}</td>
                <td className="py-2 text-ink">{p.name}</td>
                <td className="py-2 text-ink-soft">{p.category}</td>
                <td className="py-2 text-ink-soft">{p.base_unit}</td>
                <td className="py-2 text-ink-soft">{p.purchase_unit}</td>
                <td className="py-2 text-ink-soft">{p.supplier}</td>
                <td className="py-2 text-right font-mono text-ink">RM {money(p.supplier_cost)}</td>
                <td className="py-2">
                  <button type="button" data-testid={`inv-product-qr-${p.sku}`} onClick={() => setQrFor(p)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.14em] text-ink-soft hover:text-ink">
                    <QrCode size={11} /> Show
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {form && <ProductFormModal initial={form} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
      {qrFor && <QrPreviewModal product={qrFor} onClose={() => setQrFor(null)} />}
      {syncOpen && (
        <SyncFromIngredientsModal
          outlets={outlets}
          busy={syncBusy}
          result={syncResult}
          onRun={runSync}
          onClose={() => { setSyncOpen(false); setSyncResult(null); }}
        />
      )}
    </div>
  );
}

function ProductFormModal({ initial, onClose, onSaved }) {
  const [v, setV] = useState({ sku: "", name: "", category: "", base_unit: "g", purchase_unit: "pack", unit_conversion: 1, supplier: "", supplier_cost: 0, ...initial });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/inv/products", { ...v, supplier_cost: Number(v.supplier_cost || 0), unit_conversion: Number(v.unit_conversion || 1) });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-6" data-testid="inv-product-modal">
      <form onSubmit={save} className="bg-bone p-6 rounded-sm w-full max-w-lg space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl text-ink">New SKU</h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={18} /></button>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm">{err}</p>}
        <div className="grid grid-cols-2 gap-3">
          <input className={inputCls} data-testid="inv-f-sku" placeholder="SKU (e.g. MATCHA-002)" value={v.sku} onChange={(e) => setV({ ...v, sku: e.target.value.toUpperCase() })} required />
          <input className={inputCls} data-testid="inv-f-name" placeholder="Product name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} required />
          <input className={inputCls} data-testid="inv-f-cat" placeholder="Category" value={v.category} onChange={(e) => setV({ ...v, category: e.target.value })} />
          <input className={inputCls} data-testid="inv-f-supplier" placeholder="Supplier" value={v.supplier} onChange={(e) => setV({ ...v, supplier: e.target.value })} />
          <select className={inputCls} data-testid="inv-f-base" value={v.base_unit} onChange={(e) => setV({ ...v, base_unit: e.target.value })}>
            {["g","kg","ml","l","pcs","pack","carton"].map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <select className={inputCls} data-testid="inv-f-purch" value={v.purchase_unit} onChange={(e) => setV({ ...v, purchase_unit: e.target.value })}>
            {["g","kg","ml","l","pcs","pack","carton"].map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <input className={inputCls} data-testid="inv-f-conv" type="number" step="0.001" placeholder="Unit conversion (base per purchase)" value={v.unit_conversion} onChange={(e) => setV({ ...v, unit_conversion: e.target.value })} />
          <input className={inputCls} data-testid="inv-f-cost" type="number" step="0.01" placeholder="Supplier cost (RM/purchase unit)" value={v.supplier_cost} onChange={(e) => setV({ ...v, supplier_cost: e.target.value })} />
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft">Cancel</button>
          <button type="submit" disabled={busy} data-testid="inv-f-save" className="px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] rounded-full disabled:opacity-40">
            {busy ? "Saving…" : "Create SKU"}
          </button>
        </div>
      </form>
    </div>
  );
}

function QrPreviewModal({ product, onClose }) {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== "undefined" ? localStorage.getItem("matchastory.token") : "";
  const [imgSrc, setImgSrc] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${apiBase}/api/inv/products/${product.sku}/qr.png`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const blob = await resp.blob();
        if (!cancelled) setImgSrc(URL.createObjectURL(blob));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [product.sku, apiBase, token]);
  return (
    <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-6" data-testid="inv-qr-modal">
      <div className="bg-bone rounded-sm p-8 max-w-md text-center">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-ink">{product.sku}</h3>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink"><X size={18} /></button>
        </div>
        {imgSrc && <img src={imgSrc} alt={product.sku} className="mx-auto w-56 h-56" data-testid="inv-qr-img" />}
        <p className="font-display text-lg text-ink mt-2">{product.name}</p>
        <p className="text-[11px] text-ink-soft font-mono">{product.qr_code}</p>
      </div>
    </div>
  );
}

function buildQrSheetHTML(products) {
  // Each product carries a pre-fetched `dataUrl` of its QR PNG.
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8"/>
<title>Matcha Story · QR Label Sheet</title>
<style>
  @page { size: A4; margin: 15mm; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; }
  h1 { font-size: 14px; letter-spacing: 1px; margin: 0 0 12px; }
  .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .cell { border: 1px dashed #ccc; padding: 10px; text-align: center; break-inside: avoid; }
  .sku { font-family: monospace; font-size: 10px; color: #666; }
  .name { font-size: 11px; font-weight: 600; margin: 4px 0 2px; }
  .supplier { font-size: 9px; color: #888; }
  img { width: 150px; height: 150px; display: block; margin: 0 auto; }
</style>
</head>
<body>
  <h1>Matcha Story · Product QR Labels</h1>
  <div class="grid">
    ${products.map((p) => `
      <div class="cell">
        ${p.dataUrl ? `<img src="${p.dataUrl}" alt="${p.sku}"/>` : "<em>QR load failed</em>"}
        <div class="name">${p.name}</div>
        <div class="sku">${p.sku}</div>
        <div class="supplier">${p.supplier || ""}</div>
      </div>`).join("")}
  </div>
</body>
</html>`;
}


function SyncFromIngredientsModal({ outlets, busy, result, onRun, onClose }) {
  const [picked, setPicked] = useState("");

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="sync-modal">
      <div className="w-full max-w-md bg-bone rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink/10">
          <h3 className="font-display text-lg text-ink">Sync from Ingredients</h3>
          <button type="button" data-testid="sync-close" onClick={onClose} aria-label="Close" className="text-ink-soft hover:text-ink p-1">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          {!result ? (
            <>
              <p className="text-sm text-ink">
                Copy every item from the legacy <strong>Ingredients</strong> list into this catalogue (QR codes auto-assigned) AND write the current stock as the opening balance at the outlet you choose below.
              </p>
              <p className="text-[11px] text-ink-soft">
                Safe to re-run. Product rows are updated in place and stock resyncs to the latest Ingredients total.
              </p>

              <div>
                <p className="overline text-ink-soft mb-2">Which outlet holds the physical stock?</p>
                {outlets.length === 0 ? (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-3" data-testid="sync-no-outlets">
                    No outlets found. Create one under the <strong>Outlets</strong> tab first.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-2" data-testid="sync-outlet-grid">
                    {outlets.map((o) => (
                      <button
                        key={o.code}
                        type="button"
                        data-testid={`sync-outlet-${o.code}`}
                        onClick={() => setPicked(o.code)}
                        disabled={busy}
                        className={`px-4 py-3 rounded-sm text-left transition-colors border ${
                          picked === o.code
                            ? "bg-ink text-bone border-ink"
                            : "bg-white text-ink border-ink/15 hover:border-matcha-dark"
                        } disabled:opacity-50`}
                      >
                        <p className="font-mono text-xs opacity-80">{o.code}</p>
                        <p className="font-display text-base">{o.name}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="space-y-3" data-testid="sync-result">
              <p className="text-sm text-matcha-dark font-medium">Synced to {result.outlet_code}</p>
              <ul className="text-sm space-y-1 bg-white border border-ink/10 rounded-sm p-3">
                <li>Products created: <strong>{result.products_created_count}</strong></li>
                <li>Products updated: <strong>{result.products_updated_count}</strong></li>
                <li>Stock lines carried: <strong>{result.stock_carried_count}</strong></li>
                <li>Items with 0 stock: <strong>{result.stock_zero_count}</strong></li>
                {result.failed_count > 0 && <li className="text-red-700">Failed: <strong>{result.failed_count}</strong></li>}
              </ul>
              {result.stock_carried.length > 0 && (
                <div>
                  <p className="overline text-ink-soft mb-1">Stock carried</p>
                  <ul className="text-xs space-y-0.5 max-h-56 overflow-y-auto bg-white border border-ink/10 rounded-sm p-3 font-mono">
                    {result.stock_carried.map((r) => (
                      <li key={r.sku}>{r.sku} — {r.name} = <strong>{r.qty}</strong> {r.unit}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 px-5 py-4 border-t border-ink/10 bg-bone">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-ink px-4 py-2">
            {result ? "Close" : "Cancel"}
          </button>
          {!result && (
            <button
              type="button"
              data-testid="sync-run"
              onClick={() => onRun(picked)}
              disabled={!picked || busy || outlets.length === 0}
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-matcha-dark text-bone text-xs uppercase tracking-[0.22em] rounded-full disabled:opacity-40"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : null}
              {busy ? "Syncing…" : "Run Sync"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
function OutletsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [err, setErr] = useState("");
  const load = async () => {
    try { const { data } = await api.get("/inv/outlets"); setRows(data); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4" data-testid="inv-outlets-pane">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-xl text-ink">Outlets · {rows.length}</h2>
        <button type="button" data-testid="inv-outlet-new" onClick={() => setForm({})}
          className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 border border-ink/20 text-ink text-[11px] uppercase tracking-[0.22em] rounded-full hover:bg-ink/5">
          <Plus size={12} /> New Outlet
        </button>
      </div>
      {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm">{err}</p>}
      <table className="w-full text-xs" data-testid="inv-outlets-table">
        <thead className="text-left text-ink-soft border-b border-ink/10">
          <tr><th className="py-2 overline">Code</th><th className="py-2 overline">Name</th><th className="py-2 overline">Address</th><th className="py-2 overline">Status</th></tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.code} className="border-t border-ink/5" data-testid={`inv-outlet-row-${o.code}`}>
              <td className="py-2 font-mono text-ink">{o.code}</td>
              <td className="py-2 text-ink">{o.name}</td>
              <td className="py-2 text-ink-soft">{o.address}</td>
              <td className="py-2 text-[11px]">{o.active ? <span className="text-matcha-dark">ACTIVE</span> : <span className="text-ink-soft">inactive</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {form && (
        <OutletFormModal onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
      )}
    </div>
  );
}

function OutletFormModal({ onClose, onSaved }) {
  const [v, setV] = useState({ code: "", name: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/inv/outlets", v); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-ink/50 flex items-center justify-center p-6">
      <form onSubmit={save} className="bg-bone p-6 rounded-sm w-full max-w-md space-y-3">
        <h3 className="font-display text-xl text-ink mb-4">New Outlet</h3>
        {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-sm">{err}</p>}
        <input className={inputCls + " w-full"} data-testid="inv-outlet-code" placeholder="Outlet code (e.g. MS004)" value={v.code} onChange={(e) => setV({ ...v, code: e.target.value.toUpperCase() })} required />
        <input className={inputCls + " w-full"} data-testid="inv-outlet-name" placeholder="Outlet name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} required />
        <input className={inputCls + " w-full"} data-testid="inv-outlet-addr" placeholder="Address" value={v.address} onChange={(e) => setV({ ...v, address: e.target.value })} />
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-ink-soft">Cancel</button>
          <button type="submit" disabled={busy} className="px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] rounded-full disabled:opacity-40">{busy ? "Saving…" : "Create"}</button>
        </div>
      </form>
    </div>
  );
}


// =========================== PREPARE DELIVERY ===========================
function DispatchTab() {
  const [outlets, setOutlets] = useState([]);
  const [products, setProducts] = useState([]);
  const [outlet, setOutlet] = useState("");
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [o, p] = await Promise.all([api.get("/inv/outlets"), api.get("/inv/products")]);
      setOutlets(o.data); setProducts(p.data);
      if (!outlet && o.data.length) setOutlet(o.data[0].code);
    })();
  }, []); // eslint-disable-line

  const addLine = () => setLines([...lines, { sku: products[0]?.sku || "", qty_dispatched: 1, unit: products[0]?.purchase_unit || "pack", unit_cost: products[0]?.supplier_cost || 0 }]);
  const updateLine = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const createAndDispatch = async () => {
    if (lines.length === 0) { setStatus("Add at least one line"); return; }
    setBusy(true); setStatus("");
    try {
      const { data: d } = await api.post("/inv/deliveries", { outlet_code: outlet, lines, notes });
      await api.post(`/inv/deliveries/${d.id}/dispatch`);
      setStatus(`Dispatched · ${d.id} → ${outlet}`);
      setLines([]); setNotes("");
    } catch (e) { setStatus(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="inv-dispatch-pane">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="overline text-ink-soft">To outlet</label>
        <select className={inputCls} data-testid="inv-disp-outlet" value={outlet} onChange={(e) => setOutlet(e.target.value)}>
          {outlets.map((o) => <option key={o.code} value={o.code}>{o.code} · {o.name}</option>)}
        </select>
        <button type="button" data-testid="inv-disp-add" onClick={addLine} className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 border border-ink/20 text-ink text-[11px] uppercase tracking-[0.22em] rounded-full hover:bg-ink/5">
          <Plus size={12} /> Add line
        </button>
      </div>
      {lines.length === 0 && (
        <p className="text-sm text-ink-soft italic py-8 text-center">No lines yet. Click <strong>Add line</strong> to pick products and quantities.</p>
      )}
      {lines.map((l, i) => {
        const prod = products.find((p) => p.sku === l.sku);
        return (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`inv-disp-line-${i}`}>
            <select className={inputCls + " col-span-4"} value={l.sku} onChange={(e) => {
              const p = products.find((x) => x.sku === e.target.value);
              updateLine(i, { sku: e.target.value, unit: p?.purchase_unit || "pack", unit_cost: p?.supplier_cost || 0 });
            }}>
              {products.map((p) => <option key={p.sku} value={p.sku}>{p.sku} · {p.name}</option>)}
            </select>
            <input className={inputCls + " col-span-2"} type="number" step="0.01" placeholder="Qty" data-testid={`inv-disp-qty-${i}`}
              value={l.qty_dispatched} onChange={(e) => updateLine(i, { qty_dispatched: Number(e.target.value) })} />
            <input className={inputCls + " col-span-2"} placeholder="Unit" value={l.unit} onChange={(e) => updateLine(i, { unit: e.target.value })} />
            <input className={inputCls + " col-span-2"} type="number" step="0.01" placeholder="RM / unit" value={l.unit_cost} onChange={(e) => updateLine(i, { unit_cost: Number(e.target.value) })} />
            <span className="col-span-1 text-xs text-ink-soft font-mono">RM {money((l.qty_dispatched || 0) * (l.unit_cost || 0))}</span>
            <button type="button" className="col-span-1 text-ink-soft hover:text-red-600 justify-self-end" onClick={() => removeLine(i)}><X size={14} /></button>
          </div>
        );
      })}
      {lines.length > 0 && (
        <>
          <textarea className={inputCls + " w-full"} rows="2" placeholder="Delivery notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex items-center justify-between flex-wrap gap-3">
            <span className="text-sm text-ink-soft">
              Total value <strong className="text-ink">RM {money(lines.reduce((s, l) => s + (l.qty_dispatched || 0) * (l.unit_cost || 0), 0))}</strong>
            </span>
            <button type="button" data-testid="inv-disp-submit" onClick={createAndDispatch} disabled={busy}
              className="inline-flex items-center gap-2 px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] rounded-full disabled:opacity-40">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Truck size={13} />} Create & Dispatch
            </button>
          </div>
        </>
      )}
      {status && <p className="text-sm text-matcha-dark bg-matcha-dark/5 border border-matcha-dark/20 px-3 py-2 rounded-sm" data-testid="inv-disp-status">{status}</p>}
    </div>
  );
}


// =========================== TRACKER ===========================
function TrackerTab() {
  const [rows, setRows] = useState([]);
  const [outlet, setOutlet] = useState("");
  const [outlets, setOutlets] = useState([]);
  const load = async () => {
    const q = outlet ? `?outlet=${outlet}` : "";
    const [d, o] = await Promise.all([api.get(`/inv/deliveries${q}`), api.get("/inv/outlets")]);
    setRows(d.data); setOutlets(o.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [outlet]);
  const badge = (s) => {
    const map = { draft: "bg-ink/10 text-ink-soft", dispatched: "bg-amber-50 text-amber-800", partial: "bg-blue-50 text-blue-800", received: "bg-emerald-50 text-emerald-800" };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] ${map[s] || "bg-ink/10"}`}>{s}</span>;
  };
  return (
    <div className="space-y-4" data-testid="inv-tracker-pane">
      <div className="flex items-center gap-3">
        <label className="overline text-ink-soft">Outlet</label>
        <select className={inputCls} value={outlet} onChange={(e) => setOutlet(e.target.value)}>
          <option value="">All</option>
          {outlets.map((o) => <option key={o.code} value={o.code}>{o.code} · {o.name}</option>)}
        </select>
      </div>
      <table className="w-full text-xs">
        <thead className="text-left text-ink-soft border-b border-ink/10">
          <tr><th className="py-2 overline">Delivery</th><th className="py-2 overline">Outlet</th><th className="py-2 overline">Status</th><th className="py-2 overline">Lines</th><th className="py-2 overline">Dispatched</th><th className="py-2 overline">Received</th></tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan="6" className="py-6 text-center text-ink-soft">No deliveries yet.</td></tr>}
          {rows.map((d) => {
            const totD = d.lines.reduce((s, l) => s + (l.qty_dispatched || 0), 0);
            const totR = d.lines.reduce((s, l) => s + (l.qty_received || 0), 0);
            return (
              <tr key={d.id} className="border-t border-ink/5" data-testid={`inv-tracker-row-${d.id}`}>
                <td className="py-2 font-mono text-ink">{d.id}</td>
                <td className="py-2 text-ink">{d.outlet_code} · {d.outlet_name}</td>
                <td className="py-2">{badge(d.status)}</td>
                <td className="py-2 text-ink-soft">{d.lines.length}</td>
                <td className="py-2 text-right font-mono text-ink-soft">{totD}</td>
                <td className="py-2 text-right font-mono text-ink">{totR}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// =========================== BALANCES ===========================
function BalancesTab() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [outlet, setOutlet] = useState("");
  const [outlets, setOutlets] = useState([]);
  const load = async () => {
    const q = outlet ? `?outlet=${outlet}` : "";
    const [b, p, o] = await Promise.all([api.get(`/inv/balances${q}`), api.get("/inv/products"), api.get("/inv/outlets")]);
    setRows(b.data); setProducts(p.data); setOutlets(o.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [outlet]);
  const prodMap = useMemo(() => Object.fromEntries(products.map((p) => [p.sku, p])), [products]);
  return (
    <div className="space-y-4" data-testid="inv-balances-pane">
      <div className="flex items-center gap-3">
        <label className="overline text-ink-soft">Outlet</label>
        <select className={inputCls} value={outlet} onChange={(e) => setOutlet(e.target.value)}>
          <option value="">All</option>
          {outlets.map((o) => <option key={o.code} value={o.code}>{o.code} · {o.name}</option>)}
        </select>
      </div>
      <table className="w-full text-xs">
        <thead className="text-left text-ink-soft border-b border-ink/10">
          <tr><th className="py-2 overline">Outlet</th><th className="py-2 overline">SKU</th><th className="py-2 overline">Name</th><th className="py-2 overline text-right">On Hand</th><th className="py-2 overline">Base</th><th className="py-2 overline text-right">Value</th><th className="py-2 overline">Last Movement</th></tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan="7" className="py-6 text-center text-ink-soft">No stock on hand yet. Dispatch and receive a delivery to populate.</td></tr>}
          {rows.map((b, i) => (
            <tr key={i} className="border-t border-ink/5" data-testid={`inv-bal-row-${b.outlet_code}-${b.sku}`}>
              <td className="py-2 font-mono text-ink">{b.outlet_code}</td>
              <td className="py-2 font-mono text-ink">{b.sku}</td>
              <td className="py-2 text-ink">{prodMap[b.sku]?.name || ""}</td>
              <td className="py-2 text-right font-mono text-ink">{b.qty_on_hand}</td>
              <td className="py-2 text-ink-soft">{prodMap[b.sku]?.base_unit}</td>
              <td className="py-2 text-right font-mono text-ink">RM {money(b.value_on_hand)}</td>
              <td className="py-2 text-[10px] text-ink-soft">{b.last_movement ? new Date(b.last_movement).toLocaleString("en-MY") : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
